import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { DollarSign, Tag } from 'lucide-react';
import { api } from '../../utils/api';
import Header from '../shared/Header';
import Footer from '../shared/Footer';
import SEO from '../shared/SEO';

const ServicesPage = () => {
  const [categories, setCategories] = useState([]);
  const [hairstyles, setHairstyles] = useState([]);

  useEffect(() => {
    api.getCategories().then(res => setCategories(res.data)).catch(console.error);
    api.getPublicHairstyles().then(res => setHairstyles(res.data)).catch(console.error);
  }, []);

  const activeCategories = categories.filter(c => c.status === 'active');
  const upcomingCategories = categories.filter(c => c.status === 'upcoming');

  return (
    <div className="min-h-screen bg-[#FCFBF9]">
      <SEO
        title="Services"
        description="Explore our hair styling services — braiding, weaving, cornrows, wig installation, and natural hair care. Premium styles at honest prices in NGN."
        path="/services"
      />
      <Header />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h1 className="font-serif text-4xl lg:text-5xl text-[#3E2723] mb-4 tracking-tight">Our Services</h1>
            <p className="text-[#786E66] text-lg">Premium hair styling services tailored to your needs</p>
          </div>

          <div className="mb-24">
            <h2 className="font-serif text-3xl text-[#3E2723] mb-8 text-center">Active Services</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {activeCategories.map((category, idx) => {
                const categoryStyles = hairstyles.filter(s => s.category === category.name);
                const minPrice = categoryStyles.length > 0 ? Math.min(...categoryStyles.map(s => s.price_ngn)) : 0;
                
                return (
                  <motion.div
                    key={category.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E6DCD3] p-8 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all"
                    data-testid={`service-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <h3 className="font-serif text-2xl text-[#3E2723] mb-4">{category.name}</h3>
                    <div className="space-y-3 mb-6">
                      {categoryStyles.length > 0 && (
                        <>
                          <div className="flex items-center gap-2 text-[#786E66]">
                            <Tag className="h-4 w-4" />
                            <span className="text-sm">{categoryStyles.length} styles available</span>
                          </div>
                          <div className="flex items-center gap-2 text-[#786E66]">
                            <DollarSign className="h-4 w-4" />
                            <span className="text-sm">From ₦{minPrice.toLocaleString()}</span>
                          </div>
                        </>
                      )}
                    </div>
                    <Link 
                      to={`/gallery?category=${encodeURIComponent(category.name)}`}
                      className="text-[#3E2723] font-medium hover:underline"
                    >
                      View Styles →
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {upcomingCategories.length > 0 && (
            <div>
              <h2 className="font-serif text-3xl text-[#3E2723] mb-8 text-center">Coming Soon</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {upcomingCategories.map((category, idx) => (
                  <motion.div
                    key={category.name}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="bg-[#F3EBE1] rounded-2xl p-8 border border-[#E6DCD3]"
                  >
                    <span className="inline-block bg-[#C9A961] text-white text-xs px-3 py-1 rounded-full mb-4">Coming Soon</span>
                    <h3 className="font-serif text-2xl text-[#3E2723] mb-2">{category.name}</h3>
                    <p className="text-[#786E66] text-sm">Available by request. Contact us for more information.</p>
                  </motion.div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
